import React, { useEffect, useId, useState } from "react"
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext"
import { Textarea } from "@ui/shad"
import { $getNodeByKey, $insertNodes, COMMAND_PRIORITY_EDITOR, COMMAND_PRIORITY_NORMAL, createCommand } from "lexical"

import { FullscreenDialog } from "@/components/dialogs"
import { $createRawHtmlNode, $isRawHtmlNode, RawHtmlPayload } from "@/components/lexical/nodes/raw-html/node"

export const INSERT_RAW_HTML_COMMAND = createCommand<RawHtmlPayload>("INSERT_RAW_HTML_COMMAND")
export const OPEN_RAW_HTML_DIALOG_COMMAND = createCommand<{ initialHtml?: string; nodeKey?: string }>(
  "OPEN_RAW_HTML_DIALOG_COMMAND"
)

export default function RawHtmlPlugin(): React.JSX.Element | null {
  const dialogFormId = useId()

  const [editor] = useLexicalComposerContext()

  const [isOpen, setIsOpen] = useState(false)
  const [htmlInput, setHtmlInput] = useState<string>("")
  const [editingNodeKey, setEditingNodeKey] = useState<string | undefined>()

  useEffect(() => {
    // Register insert command
    const unregisterInsert = editor.registerCommand<RawHtmlPayload>(
      INSERT_RAW_HTML_COMMAND,
      (payload) => {
        const node = $createRawHtmlNode(payload)
        $insertNodes([node])
        return true
      },
      COMMAND_PRIORITY_EDITOR
    )

    const unregisterOpenDialog = editor.registerCommand<{ initialHtml?: string; nodeKey?: string }>(
      OPEN_RAW_HTML_DIALOG_COMMAND,
      (payload) => {
        setHtmlInput(payload.initialHtml || "")
        setEditingNodeKey(payload.nodeKey)
        editor.blur()
        setTimeout(() => {
          setIsOpen(true)
        }, 0)
        return true
      },
      COMMAND_PRIORITY_NORMAL
    )

    return () => {
      unregisterInsert()
      unregisterOpenDialog()
    }
  }, [editor])

  const handleSubmit = () => {
    if (!htmlInput) {
      setIsOpen(false)
      return
    }

    if (editingNodeKey) {
      // Update existing node content
      editor.update(() => {
        const node = $getNodeByKey(editingNodeKey)
        if ($isRawHtmlNode(node)) {
          node.setHtml(htmlInput)
        }
      })
    } else {
      // Insert new node
      editor.dispatchCommand(INSERT_RAW_HTML_COMMAND, { html: htmlInput })
    }

    setIsOpen(false)
  }

  const onClose = () => {
    setIsOpen(false)
    setEditingNodeKey(undefined)
    setHtmlInput("")
  }

  return (
    <>
      {isOpen && (
        <FullscreenDialog
          title={editingNodeKey ? "Edit HTML" : "Insert HTML"}
          open={isOpen}
          onOpenChange={setIsOpen}
          action={{ label: editingNodeKey ? "Update" : "Insert", form: dialogFormId }}
          cancel={{ label: "Cancel", onClick: onClose }}
        >
          <form
            id={dialogFormId}
            className="flex h-full min-h-0 flex-col"
            onSubmit={(event) => {
              event.preventDefault()
              void handleSubmit()
            }}
          >
            <Textarea
              id="raw-html-textarea"
              value={htmlInput}
              onChange={(e) => setHtmlInput(e.target.value)}
              className="min-h-0 flex-1 resize-none overflow-y-auto font-mono text-xs"
              placeholder="Paste your HTML here (e.g., email signatures, embeds, formatted content)"
              autoFocus
            />
          </form>
        </FullscreenDialog>
      )}
    </>
  )
}
